// Pure, framework-free helpers. No React, no fetch — safe to unit test in isolation.

const numberFormatter = typeof window !== "undefined"
  ? Intl.NumberFormat(window.navigator?.language || "en-US")
  : Intl.NumberFormat("en-US");

export const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return numberFormatter.format(Math.round(n));
};

export const formatRatio = (ratio) => {
  if (ratio === null || ratio === undefined || !Number.isFinite(Number(ratio))) {
    return "—";
  }
  return `${Math.round(Number(ratio))}%`;
};

export const ratioTone = (ratio) => {
  if (ratio === null || ratio === undefined) return "gray";
  if (ratio > 100) return "success";
  if (ratio >= 50) return "warning";
  return "gray";
};

export const STATUS_META = {
  ended: { label: "Ended", color: "gray" },
  ongoing: { label: "Ongoing", color: "success" },
  upcoming: { label: "Upcoming", color: "blue-light" },
};

// widget source = impressions (event card clicked inside a calendar widget),
// so the chart/legend label reads "Impressions" rather than the raw enum value.
export const SOURCE_META = {
  widget: { label: "Impressions", color: "grey-blue" },
  direct: { label: "Direct", color: "success" },
  qr: { label: "QR", color: "warning" },
  email: { label: "Email", color: "blue-light" },
};

export const SOURCE_ORDER = ["widget", "direct", "qr", "email"];

export const formatEventDateTime = (unixSeconds) => {
  if (!unixSeconds) return "—";
  const date = new Date(unixSeconds * 1000);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatBucketLabel = (bucketStartIso, resolution) => {
  const date = new Date(bucketStartIso);
  if (Number.isNaN(date.getTime())) return "";

  switch (resolution) {
    case "hourly":
      return date.toLocaleTimeString(undefined, { hour: "numeric" });
    case "daily":
      // Weekday included so patterns tied to a specific send day (e.g. a
      // Friday newsletter) are visible directly on the axis, not just
      // inferable by counting bars.
      return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    case "weekly":
      return `Wk of ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    case "monthly":
      return date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    default:
      return date.toLocaleDateString();
  }
};

export const sumSources = (sources = {}) =>
  SOURCE_ORDER.reduce((total, key) => total + (Number(sources[key]) || 0), 0);

// Given the full aggregate series and one event's series (same bucket boundaries),
// returns { aggregateTotals, eventTotals } arrays of numbers for chart rendering.
export const alignSeriesForOverlay = (aggregateSeries = [], eventSeries = []) => {
  const eventByBucket = new Map(eventSeries.map((b) => [b.bucket_start, b]));
  return aggregateSeries.map((bucket) => ({
    bucket_start: bucket.bucket_start,
    is_partial: bucket.is_partial,
    aggregateTotal: sumSources(bucket.sources),
    eventTotal: sumSources(eventByBucket.get(bucket.bucket_start)?.sources || {}),
  }));
};
